import React, { FC, Fragment } from 'react';

import { FormattedList, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { BaseStyle, BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { BroadcastMarket, CountryCode, PageName } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { Broadcast } from '~/core/types';

import { NetworkBullet, NetworkContainer, NetworkLogo, NetworkLogos, NetworkNames, Spacer } from './styles';

type Props = {
    tvBroadcasts: Array<Broadcast>;
    location: CountryCode;
    doNotSortNetworks?: boolean;
    pageName?: PageName;
};

export const LocalBroadcasts: FC<Props> = ({ tvBroadcasts, location, doNotSortNetworks }) => {
    const countryBroadcast = tvBroadcasts
        .filter((network) => network.countryCode === location.toUpperCase())
        .sort(doNotSortNetworks ? () => 0 : (a, b) => a.sequenceNumber - b.sequenceNumber);

    const localNetworks =
        countryBroadcast && countryBroadcast.filter((network) => network.market !== BroadcastMarket.national);

    return (
        localNetworks &&
        localNetworks.length > 0 && (
            <NetworkNames>
                <FormattedList value={localNetworks.map((network) => network.network)} type="unit" />
            </NetworkNames>
        )
    );
};

const TvBroadcast: FC<Props> = ({ tvBroadcasts, location, doNotSortNetworks, pageName }) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();
    const theme = useTheme();
    const isLargeScreen = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    const countryBroadcast = tvBroadcasts
        .filter((network) => network.countryCode === location.toUpperCase())
        .sort(doNotSortNetworks ? () => 0 : (a, b) => a.sequenceNumber - b.sequenceNumber);

    const localNetworks = countryBroadcast.filter((network) => network.market !== BroadcastMarket.national);
    const nationalNetworks = countryBroadcast.filter((network) => network.market === BroadcastMarket.national);
    const isGamecenter = pageName === PageName.GameCenter;
    const isScores = pageName === PageName.Scores;
    const isSchedule = pageName === PageName.Schedule;
    const isPlayoffSeries = pageName === PageName.PlayoffSeries;

    const getAltText = (network: string): string =>
        intl.formatMessage(CommonMessages.misc.whereToWatch, {
            network,
        });

    // only vertically aligns under extra large breakpoints
    const isVerticallyAligned = (isSchedule || isPlayoffSeries) && !isLargeScreen;

    // TODO: for some reason I can't just check if the theme is the same as the theme from BaseStyle, despite
    // that working in other places. Figure this out. Also use -light if should be light logo.
    const logos = nationalNetworks.map((network) => (
        <Fragment key={network.id}>
            <NetworkLogo
                src={`https://assets.nhle.com/logos/broadcast/${
                    network.id + (theme.Surface.primary === BaseStyle.DarkTheme.Surface.primary ? '-dark' : '')
                }.svg`}
                alt={getAltText(network.network)}
            />
            <NetworkBullet>&bull;</NetworkBullet>
        </Fragment>
    ));

    return (
        <NetworkContainer $isVerticallyAligned={isVerticallyAligned} $canWrap={isGamecenter}>
            {logos.length > 0 && (
                <NetworkLogos
                    $hasLocalNetworks={localNetworks.length > 0}
                    $isVerticallyAligned={isVerticallyAligned}
                    $isGamecenter={isGamecenter}
                >
                    {logos}
                </NetworkLogos>
            )}
            <Spacer />
            {!isScores && (
                <LocalBroadcasts
                    tvBroadcasts={tvBroadcasts}
                    location={location}
                    doNotSortNetworks={doNotSortNetworks}
                />
            )}
        </NetworkContainer>
    );
};

export default TvBroadcast;
